/**
 * Certificate Service
 * Handles API requests related to certificate management
 */
import { createService } from './serviceFactory';

// Define the base URL for certificate service
const baseUrl = '/api/supplier/certificates';

// Create base service with standard CRUD operations
const baseService = createService(baseUrl);

const certificateService = {
  // Include all methods from the base service
  ...baseService,
  
  /**
   * Get all certificates
   * @returns {Promise} - Response with certificates list
   */
  getCertificates() {
    return baseService.client.get(`${baseUrl}`)
      .then(response => response.data);
  },

  /**
   * Get certificate by ID
   * @param {string} id - Certificate ID
   * @returns {Promise} - Response with certificate details
   */
  getCertificateById(id) {
    if (!id) {
      console.error('getCertificateById: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Create new certificate
   * @param {Object} certificateData - Certificate data
   * @returns {Promise} - Response with created certificate
   */
  createCertificate(certificateData) {
    if (!certificateData) {
      console.error('createCertificate: certificateData is required');
      return Promise.reject(new Error('Certificate data is required'));
    }
    
    return baseService.client.post(`${baseUrl}`, certificateData)
      .then(response => response.data);
  },

  /**
   * Update certificate
   * @param {string} id - Certificate ID
   * @param {Object} certificateData - Certificate data
   * @returns {Promise} - Response with updated certificate
   */
  updateCertificate(id, certificateData) {
    if (!id) {
      console.error('updateCertificate: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    if (!certificateData) {
      console.error('updateCertificate: certificateData is required');
      return Promise.reject(new Error('Certificate data is required'));
    }
    
    return baseService.client.put(`${baseUrl}/${id}`, certificateData)
      .then(response => response.data);
  },

  /**
   * Delete certificate
   * @param {string} id - Certificate ID
   * @returns {Promise} - Response confirmation
   */
  deleteCertificate(id) {
    if (!id) {
      console.error('deleteCertificate: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    return baseService.client.delete(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Search certificates with pagination
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} - Response with search results
   */
  searchCertificates(searchParams = {}) {
    const defaultParams = {
      searchText: '',
      page: 0,
      pageSize: 20,
      sorting: '',
      code: '',
      name: ''
    };
    
    const params = { ...defaultParams, ...searchParams };
    
    return baseService.client.post(`${baseUrl}/search`, params)
      .then(response => response.data);
  },

  /**
   * Check if certificate code is unique
   * @param {string} id - Certificate ID (for edit mode)
   * @param {string} code - Certificate code to check
   * @returns {Promise} - Response with uniqueness status
   */
  isCodeUnique(id, code) {
    if (!id) {
      console.error('isCodeUnique: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    if (!code) {
      console.error('isCodeUnique: code is required');
      return Promise.reject(new Error('Certificate code is required'));
    }
    
    return baseService.client.post(`${baseUrl}/${id}/is-code-unique`, { code })
      .then(response => response.data);
  }
};

export default certificateService; 